// src/routes/RouteGuards.js
import React, { useContext } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";

/**
 * PublicRoute:
 * - If user is authenticated, redirect to their home/dashboard
 * - Otherwise render children (public page)
 */
export const PublicRoute = ({ children }) => {
  const { token, user, loading } = useContext(AuthContext);
  const location = useLocation();

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  // If logged in, choose a sensible landing page depending on their role.
  if (token && user) {
    const role = (user.role || "").toString().toLowerCase();
    const target = role === "admin" || role === "secretary" || role === "district"
      ? "/admin"
      : role === "player"
      ? "/player"
      : "/";

    // Avoid redirecting to the same path (prevents immediate re-render loops)
    if (location.pathname !== target) {
      return <Navigate to={target} replace />;
    } else {
      // already on target — render children (or null)
      return children;
    }
  }

  return children;
};

/**
 * ProtectedRoute:
 * - If not authenticated, redirect to /login
 * - If authenticated but role not allowed, redirect to login (or a 403 page)
 * - Otherwise render children
 */
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { token, user, loading } = useContext(AuthContext);
  const location = useLocation();

  if (loading) return <div className="flex items-center justify-center h-screen">Loading...</div>;

  if (!token || !user) {
    // keep attempted path in state if you want redirect after login
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (allowedRoles && allowedRoles.length > 0) {
    const role = (user.role || "").toString().toLowerCase();
    const allowed = allowedRoles.map((r) => r.toString().toLowerCase());
    if (!allowed.includes(role)) {
      return <Navigate to="/login" replace />;
    }
  }

  return children;
};

export default ProtectedRoute;
